import React from 'react';
import { useRouter } from 'next/router';
import { gql, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';

import withApollo from 'components/withApollo';
import PageLayout from 'components/PageLayout';
import Hero from 'components/Hero';
import EventCalendar from 'components/EventCalendar';
import Link from 'components/Link';
import Event from 'components/Event';
import Modal from 'components/Modal';
import EditorContent from 'components/EditorContent';

const QUERY = gql`
  query {
    allEvents(orderBy: "startDate") {
      id
      name
      description {
        document
      }
      startDate
      image {
        url: publicUrlTransformed(
          transformation: {
            width: "600"
            height: "400"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }
      attachment {
        url: publicUrl
      }
    }

    allEventsPages {
      id
      leadIn
      title
      content {
        document
      }
      heroImage {
        url: publicUrlTransformed(
          transformation: {
            width: "1440"
            height: "720"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }
    }
  }
`;

export default withApollo(() => {
  const { data } = useQuery(QUERY);
  const router = useRouter();
  const [event, setEvent] = React.useState(null);

  const page = React.useMemo(() => data?.allEventsPages?.[0], [data]);
  const events = React.useMemo(() => data?.allEvents ?? [], [data]);

  // Determine events happening in the next 90 days
  const upcomingEvents = React.useMemo(
    () =>
      events.filter((x) => {
        const diff = DateTime.fromISO(x.startDate).diffNow().as('days');
        return diff >= -1 && diff < 90;
      }),
    [events]
  );

  React.useEffect(() => {
    if (events.length > 0 && router.query.id) {
      const match = events.find((x) => x.id === router.query.id);
      if (match) setEvent(match);
    }
  }, [router, setEvent, events]);

  if (!page) return null;

  return (
    <>
      <Modal
        show={event}
        onHide={() => {
          router.push('/events');
          setEvent(null);
        }}
      >
        {event && (
          <div className="px-8 py-12 lg:p-8">
            <Event
              key={event.id}
              layout="vertical"
              date={event.startDate}
              title={event.name}
              image={event.image?.url}
              description={event.description}
              descriptionLength="full"
              attachment={event.attachment?.url}
            />
          </div>
        )}
      </Modal>
      <PageLayout title={page.title}>
        <Hero leadIn={page.leadIn} title={page.title} image={page.heroImage?.url} />

        <div className="pb-24 lg:pt-12">
          <div className="container">
            {page.content?.document && (
              <EditorContent
                component="div"
                className="text-center text-white mt-8 text-lg leading-loose max-w-4xl mx-auto"
                content={page.content}
              />
            )}

            <div className="flex flex-col lg:flex-row-reverse -ml-12 mt-12 relative">
              <div className="w-full lg:w-2/5 pl-12 pt-12">
                <div className="px-8 max-w-sm mx-auto">
                  <EventCalendar
                    events={events.map((x) => ({
                      id: x.id,
                      date: x.startDate,
                    }))}
                    onChange={(date) => {
                      const d = DateTime.fromJSDate(date).toISODate();
                      const match = events.find((x) => x.startDate === d);
                      if (!match) return;
                      router.push(`/events?id=${match.id}`);
                    }}
                  />
                  <p className="mt-4 text-center text-sm">Click on a date to see event details</p>
                </div>
              </div>
              <div className="w-full lg:w-3/5 pl-12 pt-12">
                {events.length > 0 ? (
                  <>
                    {upcomingEvents.map((x) => (
                      <div key={x.id} className="mt-12 first:mt-0">
                        <Link href={`/events?id=${x.id}`}>
                          <Event
                            key={x.id}
                            layout="horizontal"
                            date={x.startDate}
                            title={x.name}
                            image={x.image?.url}
                            description={x.description}
                            attachment={x.attachment?.url}
                          />
                        </Link>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <p className="text-lg">There are no scheduled events at this time.</p>
                    <p className="text-lg">Please check back again soon!</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
});
